import React from 'react';
import { Link } from 'react-router-dom';

import { Callout } from './shared';

const capabilities = [
  {
    icon: '🧑‍💻',
    title: 'Coding Agent',
    desc: 'Assign a task and the agent writes the code in an isolated sandbox, then opens a pull request.',
    to: '/docs/coding-agent',
  },
  {
    icon: '🔍',
    title: 'Git Reviewer',
    desc: "Reviews pull requests against your project's knowledge and architecture decisions.",
    to: '/docs/git-reviewer',
  },
  {
    icon: '🏛️',
    title: 'Knowledge Base & ADRs',
    desc: "Your team's conventions and decisions, which every agent reads before it acts.",
    to: '/docs/knowledge-base',
  },
  {
    icon: '🪄',
    title: 'Spec-to-Project Wizard',
    desc: 'Describe a project and get a ready-to-work task board in Jira, Trello, or Standalone.',
    to: '/docs/wizard',
  },
  {
    icon: '📊',
    title: 'Delivery intelligence',
    desc: 'PSI health scores, risk flags, and flow metrics that warn you before a deadline slips.',
    to: '/docs/psi',
  },
  {
    icon: '💬',
    title: 'AI assistants & MCP',
    desc: 'Ask about your projects or change them in plain language, in MFlow or from Claude.',
    to: '/docs/ai-chat',
  },
];

const flow = [
  { step: 'Plan', desc: 'The Wizard turns a description into a task board.' },
  { step: 'Build', desc: 'The Coding Agent takes tasks and opens PRs.' },
  { step: 'Review', desc: 'The Git Reviewer checks every PR.' },
  { step: 'Track', desc: 'Metrics and risk flags show how delivery is going.' },
];

const WhatIsMflow: React.FC = () => (
  <div>
    <h1 className="text-3xl font-bold mb-2">What is MFlow?</h1>
    <p className="text-base-content/60 text-sm mb-6">AI agents that build, not just track.</p>
    <p className="text-sm leading-relaxed text-base-content/80 mb-4">
      MFlow is an AI agent platform for software teams. Its agents plan the work, write the code, review the pull
      requests, and keep an eye on delivery. They all start from the same shared context: your tasks, and a Knowledge
      Base of your team&apos;s conventions and architecture decisions.
    </p>
    <p className="text-sm leading-relaxed text-base-content/80 mb-6">
      Keep your work in <strong>Jira</strong> or <strong>Trello</strong>, or use MFlow&apos;s own{' '}
      <strong>Standalone</strong> board if you don&apos;t want to connect anything. Run the agents on MFlow&apos;s
      hosted model, or bring your own OpenAI, Claude, Gemini, or DeepSeek key.
    </p>

    <h2 className="font-semibold mb-3">How it fits together</h2>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
      {flow.map((f, i) => (
        <div key={f.step} className="rounded-xl bg-base-200 shadow-card p-4 space-y-1">
          <div className="text-xs font-mono text-primary">0{i + 1}</div>
          <div className="font-semibold text-sm">{f.step}</div>
          <div className="text-xs text-base-content/60 leading-relaxed">{f.desc}</div>
        </div>
      ))}
    </div>

    <h2 className="font-semibold mb-3">What&apos;s inside</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
      {capabilities.map((c) => (
        <Link
          key={c.title}
          to={c.to}
          className="rounded-xl bg-base-200 hover:bg-base-300 shadow-card p-4 space-y-1 transition-colors"
        >
          <div className="text-xl">{c.icon}</div>
          <div className="font-semibold text-sm">{c.title}</div>
          <div className="text-xs text-base-content/60 leading-relaxed">{c.desc}</div>
        </Link>
      ))}
    </div>

    <Callout type="tip">
      New here?{' '}
      <Link to="/docs/sign-in" className="link link-primary">
        Sign in
      </Link>
      , create a{' '}
      <Link to="/docs/standalone" className="link link-primary">
        Standalone project
      </Link>{' '}
      (nothing to connect), then assign a task to the{' '}
      <Link to="/docs/coding-agent" className="link link-primary">
        Coding Agent
      </Link>
      . If you already use Jira or Trello, start with{' '}
      <Link to="/docs/connect-jira" className="link link-primary">
        Connect Jira
      </Link>{' '}
      or{' '}
      <Link to="/docs/connect-trello" className="link link-primary">
        Connect Trello
      </Link>{' '}
      instead.
    </Callout>
  </div>
);

export default WhatIsMflow;
